/**
 * Hourly Import Page
 *
 * Test page for CSV upload functionality.
 * Allows users to import Tauron hourly consumption data.
 */

import { useRef } from 'react';
import { Container, Title, Text, Stack, Paper, Divider } from '@mantine/core';
import { UploadCSV, ImportedDataList } from './components/HourlyImport';
import { useSEO } from '../../shared/hooks';
import { Layout } from '../../shared/components';
import { useAuth } from '../auth';
import { useFeatureToggle } from '../../shared/contexts';
import { HourlyImportDisabled } from '../../shared/components/FeatureDisabledPage';

export function HourlyImport() {
  useSEO({ title: 'Import danych godzinowych', noindex: true });
  const { isAuthenticated } = useAuth();

  // Feature toggle check
  const isHourlyImportEnabled = useFeatureToggle('hourly_import_enabled', true);

  // Ref to scroll to imported data list after upload
  const importedDataRef = useRef<HTMLDivElement>(null);

  // Early return if feature is disabled (AFTER all hooks are called)
  if (!isHourlyImportEnabled) {
    return <HourlyImportDisabled />;
  }

  const handleImportSuccess = () => {
    // Scroll to imported data list with smooth animation
    setTimeout(() => {
      importedDataRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 500); // Small delay to allow UI updates
  };

  return (
    <Layout isAuthenticated={isAuthenticated}>
      <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <div>
          <Title order={1} mb="md">
            Zużycie prądu na podstawie danych z licznika
          </Title>
          <Text size="lg" c="dimmed">
            Przeglądaj i zarządzaj danymi z raportu godzinowego Tauron eLicznik
          </Text>
        </div>

        {/* Imported Data List - moved to top */}
        <div ref={importedDataRef}>
          <ImportedDataList />
        </div>

        {/* Divider */}
        <Divider />

        {/* Upload Component - moved below */}
        <UploadCSV onImportSuccess={handleImportSuccess} />

        {/* Info - moved below */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="xs">
            <Text fw={600}>Jak pobrać plik CSV z Tauron eLicznik?</Text>
            <ol style={{ marginLeft: '1.5rem' }}>
              <li>
                <Text size="sm">Zaloguj się na{' '}
                  <a href="https://elicznik.tauron-dystrybucja.pl" target="_blank" rel="noopener noreferrer">
                    elicznik.tauron-dystrybucja.pl
                  </a>
                </Text>
              </li>
              <li>
                <Text size="sm">Przejdź do: Raporty → Raport godzinowy</Text>
              </li>
              <li>
                <Text size="sm">Wybierz zakres dat (np. cały rok 2025)</Text>
              </li>
              <li>
                <Text size="sm">Kliknij "Pobierz CSV"</Text>
              </li>
              <li>
                <Text size="sm">Prześlij pobrany plik poniżej</Text>
              </li>
            </ol>
          </Stack>
        </Paper>
      </Stack>
    </Container>
    </Layout>
  );
}
